import { useAccommodationsConsoleStateInitializationQuery } from '@graphql/generated';
import { useStaysSearchParams } from '@apps/stays/components/SearchParamsObserver/SearchParamsObserver';
import { useHistory, useLocation } from '@react-router';
import { useQueryParamHelper } from '@shared/core/queryString';
import { useMemo, useRef } from 'react';
import { createQueryParamsUpdater, getIsValidLatitudeAndLongitude } from '../../AccommodationsConsole.utils';
import { PICKED_BY_COUPLE_KEY } from '../useListingFilters';
import { AccommodationsConsoleState } from '../../AccommodationsConsole.types';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { ChangedLocation } from '../../components/Map/Map.types';
import { DEFAULT_SEARCH_RADIUS } from './constants';
import { QueryParamsUrlUpdater, createSearchParamsUrlUpdater, deriveRoomsQueryParams, deriveSearchDateQueryParams } from './utils';
import { useDeriveLocationQueryParams } from './useDeriveLocationQueryParams';
import { StaysSearchTerms } from '@apps/stays/types';
import { formatDateComponentsToString } from '@shared/utils/dateHelpers';
import { useTranslation } from '@shared/core';

export type UseAccommodationsConsoleSearchParamsStateReturn = ReturnType<typeof useAccommodationsConsoleSearchParamsState>;

export const useAccommodationsConsoleSearchParamsState = (args: { eventHandle: Maybe<string> }) => {
  const { eventHandle } = args;

  const shouldSkipBestLocationAnchorQuery = !eventHandle;
  const { t } = useTranslation('accommodationsConsole');

  const location = useLocation();
  const history = useHistory();

  const { data, loading, called } = useAccommodationsConsoleStateInitializationQuery({
    variables: {
      name: eventHandle || ''
    },
    skip: shouldSkipBestLocationAnchorQuery,
    fetchPolicy: 'cache-first',
    batchMode: 'fast'
  });

  const queryParamHelper = useQueryParamHelper();
  const staysSearchParams = useStaysSearchParams();
  const deriveLocationParams = useDeriveLocationQueryParams(data?.eventByName?.bestLocationAnchor, loading, called);

  /**
   * This ref is used to track if the state has been initialized.
   * on first mount, start from defaults -- otherwise, read from query params.
   */
  const isInitializedRef = useRef(false);

  const shouldProcessState = !shouldSkipBestLocationAnchorQuery || data || (!loading && !data);

  const applyUpdatesToUrl = useEventCallback<QueryParamsUrlUpdater>(args => {
    createSearchParamsUrlUpdater(location, history)(args);
  });

  const checkIfShouldInitializeStateWithDefaults = useEventCallback(() => {
    return !isInitializedRef.current;
  });

  const concludeStateInitialization = useEventCallback(() => {
    isInitializedRef.current = true;
  });

  const state = useMemo<AccommodationsConsoleState | undefined>(() => {
    if (shouldProcessState) {
      /**
       * Reset the accommodation console to the default state --
       * the idea is that users would share the accommodation link, not the search link.
       */
      const shouldUseDefaults = checkIfShouldInitializeStateWithDefaults();
      const params = new URLSearchParams(location.search);
      const updateQueryParams = createQueryParamsUpdater(params);

      updateQueryParams(PICKED_BY_COUPLE_KEY, null);

      // Set the startDate + endDate
      const datesQp = deriveSearchDateQueryParams({
        dateSearchParams: staysSearchParams,
        eventFinalizedDateString: data?.eventByName?.info?.finalizedDate?.dateString,
        useDefaults: shouldUseDefaults
      });

      // Set occupancy
      const roomsQp = deriveRoomsQueryParams({ roomsSearchParams: staysSearchParams, useDefaults: shouldUseDefaults });

      // Set Location
      const locationQp = deriveLocationParams({ locationSearchParams: staysSearchParams, useDefaults: shouldUseDefaults });

      const searchRadiusQp = queryParamHelper.getValueInteger('radius');

      // Only update URL if this is a new location and parameters are missing
      if (
        (datesQp.isMissingInUrl || roomsQp.isMissingInUrl || locationQp?.isMissingInUrl || typeof searchRadiusQp !== 'number') &&
        locationQp &&
        getIsValidLatitudeAndLongitude(locationQp?.lat, locationQp?.lng)
      ) {
        concludeStateInitialization();
        applyUpdatesToUrl({
          dates: {
            start: datesQp.startDate,
            end: datesQp.endDate
          },
          map: {
            destination: locationQp?.destination,
            lat: locationQp.lat,
            lng: locationQp.lng,
            radius: DEFAULT_SEARCH_RADIUS
          },
          rooms: roomsQp.rooms,

          actionType: 'replace'
        });
      }

      const updatedState: AccommodationsConsoleState = {
        circularRegionSearch:
          locationQp && getIsValidLatitudeAndLongitude(locationQp.lat, locationQp.lng)
            ? {
                latitude: locationQp.lat,
                longitude: locationQp.lng,
                radius: searchRadiusQp ?? DEFAULT_SEARCH_RADIUS
              }
            : null,
        searchLocation: locationQp?.destination,
        searchDates: {
          start: datesQp.startDate,
          end: datesQp.endDate
        },
        occupancies: {
          unitCount: roomsQp.rooms.length,
          occupancies: roomsQp.rooms
        }
      };

      return updatedState;
    }

    return undefined;
  }, [
    checkIfShouldInitializeStateWithDefaults,
    concludeStateInitialization,
    shouldProcessState,
    data,
    staysSearchParams,
    queryParamHelper,
    location.search,
    applyUpdatesToUrl,
    deriveLocationParams
  ]);

  /**
   * For when the user clicks the "search this area" button on the map.
   */
  const handleOnChangeLocation = useEventCallback((changedLocation: ChangedLocation) => {
    applyUpdatesToUrl({
      map: {
        lat: changedLocation.location.lat,
        lng: changedLocation.location.lng,
        radius: changedLocation.radius,
        destination: t('map', 'areaSearchLocationValue')()
      },
      pickedByCouple: false,
      actionType: 'push'
    });
  });

  const handleHeaderSearchFormSubmit = useEventCallback((value: StaysSearchTerms) => {
    applyUpdatesToUrl({
      map: {
        lat: value.lat,
        lng: value.lng,
        destination: value.destination
      },
      dates: {
        start: formatDateComponentsToString(value.startDate),
        end: formatDateComponentsToString(value.endDate)
      },
      rooms: value.rooms,
      pickedByCouple: false,
      actionType: 'push'
    });
  });

  return {
    state,
    loading,
    handleOnChangeLocation,
    handleHeaderSearchFormSubmit
  };
};
